import { useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FolderModalTypes } from 'domains/core/components/folders/types';
import { ContentModalContentType, ContentTypeForComparison, ContentTypeEnumForBackend } from 'domains/content/types';
import MoreOptionsDropdown from 'domains/core/components/MoreOptionsDropdown';
import useContentQueryManager from 'hooks/useContentQueryParams';
import { removeTypeFromId } from 'utils/folders';
import { getContentCreatePath, contentTypeToSnakeCase } from './utils';
import { ContentStatus } from './ContentStatusText';
import useUpdateContentStatus from 'hooks/mutations/useUpdateContentStatus';

type Props = {
    handleModalOpen: (content: ContentModalContentType, modalType: FolderModalTypes) => void;
    val: any;
};

const useGetMoreOptionsCell = ({ handleModalOpen, val }: Props) => {
    const history = useHistory();
    const { archiveFlag: hasArchiveFlag } = useFlags();

    const { changeViewParams } = useContentQueryManager();

    const { folderId: parentFolderId } = useContentQueryManager();

    const handleDeleteModalOpen = (contentData: ContentModalContentType) =>
        handleModalOpen(contentData, FolderModalTypes.DELETE);

    const handleArchiveModalOpen = (contentData: ContentModalContentType) => {
        handleModalOpen(contentData, FolderModalTypes.ARCHIVE);
    };

    const handleUnarchiveModalOpen = (contentData: ContentModalContentType) => {
        handleModalOpen(contentData, FolderModalTypes.UNARCHIVE);
    };

    const shouldShowDeleteOption = val.row.original.canDelete;

    let menuItems;

    const contentId = removeTypeFromId(val.row.original.id);
    const contentData = {
        ...val.row.original,
        id: contentId,
    };

    const status = val.row.original.status;
    const isPublished = status === ContentStatus.PUBLISHED;
    const contentType = val.row.original.type;

    // Hook must be called at top level, not conditionally
    const { mutate: updateContentStatus } = useUpdateContentStatus(contentId.toString(), {
        onSuccess: () => {
            if (val.row.original.type !== ContentTypeForComparison.FOLDER) {
                // Navigate to builder page - status will be fetched from the content API
                const path = getContentCreatePath({
                    contentType: val.row.original.type,
                    id: contentId,
                    parentFolderId,
                    status: ContentStatus.DRAFT,
                });
                history.push(path);
            }
        },
    });

    const deleteOption = {
        name: 'Delete',
        onClick: () => handleDeleteModalOpen(contentData),
    };

    const archiveOption = {
        name: 'Archive',
        onClick: () => handleArchiveModalOpen(contentData),
    };

    const unarchiveOption = {
        name: 'Unarchive',
        onClick: () => handleUnarchiveModalOpen(contentData),
    };

    if (val.row.original.type === ContentTypeForComparison.FOLDER) {
        const openOption = {
            name: 'Open',
            onClick: () => changeViewParams({ newFolderId: contentId }),
        };
        const renameOption = {
            name: 'Rename',
            onClick: () => handleModalOpen(contentData, FolderModalTypes.RENAME),
        };
        menuItems = val.row.original.canDelete ? [openOption, renameOption, deleteOption] : [openOption, renameOption];
    } else {
        const editOption = {
            name: 'Edit',
            onClick: () => {
                if (isPublished) {
                    // Convert published to draft before navigating
                    const channelForBackend = contentTypeToSnakeCase(
                        contentType as keyof typeof ContentTypeEnumForBackend
                    );

                    updateContentStatus({
                        status: ContentStatus.DRAFT,
                        channel: channelForBackend,
                    });
                } else {
                    // Navigate to builder page - status will be fetched from the content API
                    const path = getContentCreatePath({
                        contentType,
                        id: contentId,
                        parentFolderId,
                    });
                    history.push(path);
                }
            },
        };
        const copyOption = {
            name: 'Copy',
            onClick: () => {
                // Copy creates a new draft - status will be set in builder page
                const path = getContentCreatePath({
                    contentType: val.row.original.type,
                    id: contentId,
                    isCopy: true,
                    parentFolderId,
                });
                history.push(path);
            },
        };
        const moveOption = {
            name: 'Move',
            onClick: () => {
                handleModalOpen(val.row.original, FolderModalTypes.MOVE);
            },
        };

        // Show archive/unarchive option based on isArchived flag and archive feature flag
        const shouldShowArchiveOption = hasArchiveFlag && val.row.original.type !== 'Folder';
        const isArchived = val.row.original.isArchived;
        const isLive = status === ContentStatus.LIVE;

        let archiveMenuItems: any[] = [];
        if (shouldShowArchiveOption) {
            if (isArchived) {
                archiveMenuItems = [unarchiveOption];
            } else {
                archiveMenuItems = [archiveOption];
            }
        }

        // Don't show move option for archived items
        const shouldShowMoveOption = !isArchived;
        const moveMenuItems = shouldShowMoveOption ? [moveOption] : [];

        // If status is live, only show copy and move options
        if (isLive) {
            menuItems = shouldShowMoveOption ? [copyOption, moveOption] : [copyOption];
        } else {
            menuItems = shouldShowDeleteOption
                ? [editOption, copyOption, ...moveMenuItems, ...archiveMenuItems, deleteOption]
                : [editOption, ...moveMenuItems, copyOption, ...archiveMenuItems];
        }
    }

    return <MoreOptionsDropdown menuItems={menuItems} />;
};

export default useGetMoreOptionsCell;
